import { Step } from "../components/types";
import config from "../configLoader";
import "../utils/momentToJSONConverter";
import mock from "./mock-get-document.json";
import mockFollowupQuestionnaire from "../mock-followup-questionnaire.json";
import { flushInteractions } from "./interactionBuffer";

export async function loginUser(postData: any) {
  const data = await fetchData("POST", "/public-auth", postData);
  return data;
}

export async function getQuestionnaire(step: Step) {
  const formDefResponse = await fetch(`/${step}.json` + "?" + Math.random());
  const formDefTmp = await formDefResponse.json();
  return formDefTmp;
}

export const submitQuestionnaireForm = (data: Record<string, unknown>) => {
  const url = "/npp/questionnaire-response";
  return fetchData("POST", url, data);
};

export interface StaticDocumentResponse {
  data: {
    document?: { id: number; title: string; html: string };
    errorMessage?: string;
  };
}

// Read-only legal documents (Terms / Privacy / ESIGN consent) served by the EMR
// from templates mirrored out of Google Docs. Name must match a server whitelist.
export async function getStaticDocument(
  name: string,
): Promise<StaticDocumentResponse> {
  return fetchData("POST", "/npp/get-static-document", { name });
}

enum ContentType {
  JSON = "json",
  BLOB = "blob",
  TEXT = "text",
  OTHER = "other",
}

export async function fetchData(httpMethod: string, url: string, data: any) {
  const telemetry = flushInteractions();
  const dataWithFingerprint = {
    ...data,
    fingerprint: generateFingerPrint(),
    ...(telemetry ? { _telemetry: telemetry } : {}),
  };
  try {
    const response = await fetch(`${config.baseUrl}${url}`, {
      method: httpMethod,
      body: JSON.stringify(dataWithFingerprint),
      headers: {
        "Content-Type": "application/json",
        "Api-Key": config.apiKey,
      },
    });
    const contentType = determineContentType(response);

    if (contentType !== ContentType.JSON) {
      console.error("Response content type is not JSON");
      throw new Error("Response content type is not JSON");
    }

    if (response.ok) {
      return await response.json();
    }
    if (response.status >= 500 && response.status < 600) {
      const data = {
        data: {
          errorMessage: `We're sorry, but our server encountered an error. Please try again in a few minutes.
          \n
          Status ${response.status}`,
        },
      };
      console.error("Error response:", data);
      return data;
    }
    throw new Error(`Something went wrong. Please try again`);
  } catch (error) {
    console.error("Error fetching data:", error);
    throw error;
  }
}

function generateFingerPrint(): string {
  let _navigator: Record<string, any> = {};
  for (const i in navigator) {
    _navigator[i] = (navigator as any)[i];
  }
  return btoa(JSON.stringify(_navigator));
}

function determineContentType(response: Response): ContentType {
  if (!response || !response.headers) {
    throw new Error("Something went wrong. Please try again.");
  }
  const contentType = response.headers.get("Content-Type");
  if (contentType?.includes("json")) {
    return ContentType.JSON;
  } else if (contentType?.includes("blob")) {
    return ContentType.BLOB;
  } else if (contentType?.includes("text")) {
    return ContentType.TEXT;
  }
  return ContentType.OTHER;
}
